package it.unibo.tuprolog.solve.libs.oop.primitives

import it.unibo.tuprolog.core.Struct
import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.core.Var
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.exception.error.InstantiationError
import it.unibo.tuprolog.solve.libs.oop.rules.Alias
import it.unibo.tuprolog.solve.primitive.Solve
import it.unibo.tuprolog.solve.primitive.UnaryPredicate

/**
 * `unregister(+Alias)`: removes every [it.unibo.tuprolog.solve.libs.oop.rules.Alias] fact
 * matching the ground alias term `Alias` from the current solver's static knowledge base,
 * undoing a previous [Register].
 *
 * Fails if `Alias` is not currently registered.
 *
 * @throws it.unibo.tuprolog.solve.exception.error.InstantiationError if `Alias` is unbound or not ground.
 */
object Unregister : UnaryPredicate.NonBacktrackable<ExecutionContext>("unregister") {
    override val help: String =
        """
        `unregister(+Alias)`
        
        Removes every `alias(Alias, _)` fact for the ground term `Alias` from the static knowledge base, undoing `register/2` (it can also remove the library's default aliases, such as `string` or `stdout`). Fails if no such alias is registered. Raises a type error if `Alias` is unbound or not callable, and an instantiation error if it is not ground.

        **Examples**

        ```prolog
        ?- new_object('java.util.ArrayList', [], L), register(L, my_list).
        yes.

        ?- unregister(my_list).
        yes.

        ?- alias(my_list, R).
        no.

        ?- unregister(my_list).
        no.
        ```
        """.trimIndent()

    override fun Solve.Request<ExecutionContext>.computeOne(first: Term): Solve.Response {
        ensuringArgumentIsStruct(0)
        if (!first.isGround) {
            throw InstantiationError.forArgument(context, signature, first.variables.first(), 0)
        }
        val pattern = Struct.of(Alias.FUNCTOR, first, Var.anonymous())
        val toBeRemoved = context.staticKb[pattern].filter { it.head[0] == first }.toList()
        return if (toBeRemoved.isEmpty()) {
            replyFail()
        } else {
            replySuccess {
                removeStaticClauses(toBeRemoved)
            }
        }
    }
}
